import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { materialsAPI, customWorkTypesAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Loader2, RefreshCw, Edit2, X, Package } from 'lucide-react';

const RATE_GROUPS = {
    'Core Plastering': [
        { key: 'skimWalls_rate_gbp_per_m2', name: 'Blue Grit & Overskim (Walls)', unit: 'm²' },
        { key: 'boardAndSkimWalls_rate_gbp_per_m2', name: 'Board & Overskim (Walls)', unit: 'm²' },
        { key: 'dotdab_skim_rate_gbp_per_m2', name: 'Dot & Dab & Skim (Walls)', unit: 'm²' },
        { key: 'skimCeilings_rate_gbp_per_m2', name: 'Blue Grit & Overskim (Ceiling)', unit: 'm²' },
        { key: 'boardAndSkimCeilings_rate_gbp_per_m2', name: 'Board & Overskim (Ceiling)', unit: 'm²' },
        { key: 'patching_rate_gbp_per_m2', name: 'Patching', unit: 'm²' },
        { key: 'bonding_rate_gbp_per_m2', name: 'Bonding', unit: 'm²' },
    ],
    'Boarding': [
        { key: 'overboardCeiling_rate_gbp_per_m2', name: 'Overboard Ceiling', unit: 'm²' },
    ],
    'Insulation & Prep': [
        { key: 'insulate_rate_gbp_per_m2', name: 'Insulation (Walls / Ceiling)', unit: 'm²' },
        { key: 'removeLoosePlaster_rate_gbp_per_m2', name: 'Remove Loose Plaster', unit: 'm²' },
    ],
    'Materials per Metre': [
        { key: 'beads_rate_gbp_per_m', name: 'Plaster Beads', unit: 'm' },
        { key: 'scrimTape_rate_gbp_per_m', name: 'Scrim Tape', unit: 'm' },
    ],
    'Rendering': [
        { key: 'renderSandCement_rate_gbp_per_m2', name: 'Sand & Cement Render', unit: 'm²' },
        { key: 'renderSilicone_rate_gbp_per_m2', name: 'Silicone Render', unit: 'm²' },
        { key: 'renderMonocouche_rate_gbp_per_m2', name: 'Monocouche Render', unit: 'm²' },
        { key: 'renderKRend_rate_gbp_per_m2', name: 'K-Rend', unit: 'm²' },
        { key: 'renderTyrolean_rate_gbp_per_m2', name: 'Tyrolean / Dry Dash', unit: 'm²' },
    ],
};

const EMPTY_MATERIAL = { name: '', category: '', unit: 'bag', unit_cost: '', coverage_per_unit: '1', notes: '' };
const CATEGORIES = ['Plaster', 'Boards', 'Insulation', 'Adhesive / Bonding', 'Render', 'Beads & Tape', 'Other'];
const UNITS = ['bag', 'sheet', 'roll', 'tub', 'bucket', 'board', 'length', 'each'];

// Default category pre-fill when adding from within a group card
const GROUP_DEFAULT_CATEGORY = {
    'Core Plastering': 'Plaster',
    'Boarding': 'Boards',
    'Insulation & Prep': 'Insulation',
    'Materials per Metre': 'Beads & Tape',
    'Rendering': 'Render',
};

export const AdminMaterials = () => {
    const navigate = useNavigate();
    const [materials, setMaterials] = useState([]);
    const [compositions, setCompositions] = useState({});
    const [labourHours, setLabourHours] = useState({});
    const [customWorkTypes, setCustomWorkTypes] = useState([]); // custom user-created work types
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [recalculating, setRecalculating] = useState(false);

    // Material form state (Materials tab)
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [activeTab, setActiveTab] = useState('materials');
    const [matForm, setMatForm] = useState(EMPTY_MATERIAL);

    // Inline quick-add form state (Compositions tab)
    const [inlineAddGroup, setInlineAddGroup] = useState(null);
    const [inlineWorkTypeForm, setInlineWorkTypeForm] = useState({ name: '', survey_context: 'wall', job_type_compat: 'Plastering' });
    const [savingInline, setSavingInline] = useState(false);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        try {
            const [matsRes, compsRes, customRes] = await Promise.all([
                materialsAPI.listMaterials(),
                materialsAPI.listCompositions(),
                customWorkTypesAPI.list()
            ]);
            setMaterials(matsRes.data);
            setCustomWorkTypes(customRes.data);
            const compMap = {};
            const labourMap = {};
            compsRes.data.forEach(c => {
                compMap[c.rate_key] = c.components || [];
                labourMap[c.rate_key] = c.labour_hours_per_unit || 0;
            });
            setCompositions(compMap);
            setLabourHours(labourMap);
        } catch { toast.error('Failed to load materials'); }
        finally { setLoading(false); }
    };

    const fmt = (v) => `£${(+v || 0).toFixed(2)}`;

    // ── Material CRUD ──────────────────────────────────────────────────────────

    const openAddMaterial = () => { setMatForm(EMPTY_MATERIAL); setEditingMaterial('new'); };
    const openEditMaterial = (m) => {
        setMatForm({ ...m, unit_cost: String(m.unit_cost), coverage_per_unit: String(m.coverage_per_unit) });
        setEditingMaterial(m.id);
    };

    const handleSaveMaterial = async () => {
        if (!matForm.name.trim()) { toast.error('Name required'); return; }
        setSaving(true);
        try {
            const payload = {
                ...matForm,
                unit_cost: parseFloat(matForm.unit_cost) || 0,
                coverage_per_unit: parseFloat(matForm.coverage_per_unit) || 1,
            };
            if (editingMaterial === 'new') {
                const res = await materialsAPI.createMaterial(payload);
                setMaterials(prev => [...prev, res.data]);
                toast.success('Material added');
            } else {
                const res = await materialsAPI.updateMaterial(editingMaterial, payload);
                setMaterials(prev => prev.map(m => m.id === editingMaterial ? res.data : m));
                toast.success('Material updated');
            }
            setEditingMaterial(null);
        } catch { toast.error('Failed to save material'); }
        finally { setSaving(false); }
    };

    const handleDeleteMaterial = async (id) => {
        try {
            await materialsAPI.deleteMaterial(id);
            setMaterials(prev => prev.filter(m => m.id !== id));
            const updated = {};
            Object.entries(compositions).forEach(([key, comps]) => {
                updated[key] = comps.filter(c => c.material_id !== id);
            });
            setCompositions(updated);
            toast.success('Material deleted');
        } catch { toast.error('Failed to delete'); }
    };

    // ── Inline quick-add from Compositions tab ─────────────────────────────────

    const openInlineAdd = (groupName) => {
        setInlineWorkTypeForm({ name: '', survey_context: 'wall', job_type_compat: 'Plastering' });
        setInlineAddGroup(groupName);
    };

    const handleSaveInline = async () => {
        if (!inlineWorkTypeForm.name.trim()) { toast.error('Name required'); return; }
        setSavingInline(true);
        try {
            const res = await customWorkTypesAPI.create({
                name: inlineWorkTypeForm.name.trim(),
                group: inlineAddGroup,
                survey_context: inlineWorkTypeForm.survey_context,
                job_type_compat: inlineWorkTypeForm.job_type_compat,
            });
            setCustomWorkTypes(prev => [...prev, res.data]);
            setInlineAddGroup(null);
            toast.success(`"${res.data.name}" added — compose it below and hit Recalculate`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create work type');
        } finally { setSavingInline(false); }
    };

    const handleDeleteCustomWorkType = async (id) => {
        try {
            await customWorkTypesAPI.delete(id);
            setCustomWorkTypes(prev => prev.filter(wt => wt.id !== id));
            toast.success('Work type removed');
        } catch { toast.error('Failed to remove'); }
    };

    // ── Composition editing ────────────────────────────────────────────────────

    // Helper: expected qty from material's current coverage
    const expectedQty = (materialId) => {
        const mat = materials.find(m => m.id === materialId);
        if (!mat) return null;
        return Math.round((1 / (mat.coverage_per_unit || 1)) * 10000) / 10000;
    };

    // Check if a component's stored qty is out of sync with current coverage
    const isOutOfSync = (comp) => {
        const expected = expectedQty(comp.material_id);
        if (expected === null) return false;
        return Math.abs((comp.quantity_per_unit || 0) - expected) > 0.0001;
    };

    // Sync a single component to current coverage
    const syncComponentToCoverage = (rateKey, materialId) => {
        const qty = expectedQty(materialId);
        if (qty === null) return;
        setCompositions(prev => ({
            ...prev,
            [rateKey]: (prev[rateKey] || []).map(c =>
                c.material_id === materialId ? { ...c, quantity_per_unit: qty } : c
            )
        }));
    };

    // Sync ALL components across all rate keys to current coverage
    const syncAllToCoverage = () => {
        setCompositions(prev => {
            const updated = {};
            Object.entries(prev).forEach(([rateKey, comps]) => {
                updated[rateKey] = comps.map(c => {
                    const qty = expectedQty(c.material_id);
                    return qty !== null ? { ...c, quantity_per_unit: qty } : c;
                });
            });
            return updated;
        });
        toast.success('All quantities synced to current coverage');
    };

    const addComponent = (rateKey, materialId) => {
        if (!materialId) return;
        const current = compositions[rateKey] || [];
        if (current.some(c => c.material_id === materialId)) {
            toast.error('Material already added to this rate');
            return;
        }
        const mat = materials.find(m => m.id === materialId);
        const defaultQty = mat ? Math.round((1 / (mat.coverage_per_unit || 1)) * 10000) / 10000 : 1;
        setCompositions(prev => ({
            ...prev,
            [rateKey]: [...(prev[rateKey] || []), { material_id: materialId, quantity_per_unit: defaultQty, wastage_percent: 0, material_name: mat?.name, unit: mat?.unit }]
        }));
    };

    const updateComponentQty = (rateKey, materialId, qty) => {
        setCompositions(prev => ({
            ...prev,
            [rateKey]: (prev[rateKey] || []).map(c =>
                c.material_id === materialId ? { ...c, quantity_per_unit: parseFloat(qty) || 0 } : c
            )
        }));
    };

    const updateComponentWastage = (rateKey, materialId, wastage) => {
        setCompositions(prev => ({
            ...prev,
            [rateKey]: (prev[rateKey] || []).map(c =>
                c.material_id === materialId ? { ...c, wastage_percent: parseFloat(wastage) || 0 } : c
            )
        }));
    };

    const removeComponent = (rateKey, materialId) => {
        setCompositions(prev => ({
            ...prev,
            [rateKey]: (prev[rateKey] || []).filter(c => c.material_id !== materialId)
        }));
    };

    // Count out-of-sync components across all compositions
    const outOfSyncCount = Object.values(compositions)
        .flat()
        .filter(c => isOutOfSync(c)).length;

    const handleSaveCompositions = async () => {
        setSaving(true);
        try {
            // Standard rate keys from RATE_GROUPS
            const standardKeys = Object.values(RATE_GROUPS).flat().map(r => r.key);
            // Custom work type rate keys
            const customKeys = customWorkTypes.map(wt => wt.rate_key);
            const allKeys = [...new Set([...standardKeys, ...customKeys])];

            const saves = allKeys.map(rateKey =>
                materialsAPI.upsertComposition(rateKey, {
                    rate_key: rateKey,
                    components: compositions[rateKey] || [],
                    labour_hours_per_unit: labourHours[rateKey] || 0,
                })
            );
            await Promise.all(saves);
            toast.success('Compositions saved');
        } catch { toast.error('Failed to save compositions'); }
        finally { setSaving(false); }
    };

    const handleRecalculate = async () => {
        setRecalculating(true);
        try {
            const res = await materialsAPI.recalculate();
            toast.success(res.data.message);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to recalculate');
        } finally { setRecalculating(false); }
    };

    if (loading) return (
        <Layout>
            <div className="space-y-4 max-w-3xl mx-auto">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-64 rounded-xl" />
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="space-y-4 max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">Materials</h1>
                        <p className="text-slate-500 text-sm">Price list & rate compositions</p>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full grid grid-cols-2 h-11">
                        <TabsTrigger value="materials" data-testid="tab-materials">Materials Price List</TabsTrigger>
                        <TabsTrigger value="compositions" data-testid="tab-compositions">Rate Compositions</TabsTrigger>
                    </TabsList>

                    {/* ── TAB: MATERIALS ─────────────────────────────────────────────── */}
                    <TabsContent value="materials" className="space-y-3 mt-4">
                        <Button onClick={openAddMaterial} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="add-material-btn">
                            <Plus className="h-4 w-4 mr-2" /> Add Material
                        </Button>

                        {/* Add/Edit form */}
                        {editingMaterial && (
                            <Card className="border-red-200 bg-red-50/20">
                                <CardContent className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-sm text-slate-800">{editingMaterial === 'new' ? 'New Material' : 'Edit Material'}</p>
                                        <button onClick={() => setEditingMaterial(null)}><X className="h-4 w-4 text-slate-400" /></button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="col-span-2 space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Name *</Label>
                                            <Input value={matForm.name} onChange={e => setMatForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Thistle Multi-Finish 25kg" className="h-10 border-slate-300" data-testid="mat-name-input" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Category</Label>
                                            <select value={matForm.category} onChange={e => setMatForm(p => ({ ...p, category: e.target.value }))}
                                                className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white" data-testid="mat-category-select">
                                                <option value="">Select...</option>
                                                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Unit</Label>
                                            <select value={matForm.unit} onChange={e => setMatForm(p => ({ ...p, unit: e.target.value }))}
                                                className="w-full h-10 px-3 text-sm border border-slate-300 rounded-md bg-white" data-testid="mat-unit-select">
                                                {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Unit Cost (£)</Label>
                                            <Input type="number" step="0.01" min="0" value={matForm.unit_cost} onChange={e => setMatForm(p => ({ ...p, unit_cost: e.target.value }))}
                                                placeholder="8.50" className="h-10 border-slate-300" data-testid="mat-cost-input" />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Coverage (m² per unit)</Label>
                                            <Input type="number" step="0.1" min="0.1" value={matForm.coverage_per_unit} onChange={e => setMatForm(p => ({ ...p, coverage_per_unit: e.target.value }))}
                                                placeholder="5.0" className="h-10 border-slate-300" data-testid="mat-coverage-input" />
                                        </div>
                                        <div className="col-span-2 space-y-1">
                                            <Label className="text-xs font-bold uppercase text-slate-500">Notes</Label>
                                            <Input value={matForm.notes} onChange={e => setMatForm(p => ({ ...p, notes: e.target.value }))}
                                                placeholder="Optional notes..." className="h-10 border-slate-300" />
                                        </div>
                                    </div>
                                    {matForm.unit_cost && matForm.coverage_per_unit && (
                                        <p className="text-xs text-slate-500 bg-slate-100 rounded px-3 py-1.5">
                                            Cost per m²: <strong>{fmt((parseFloat(matForm.unit_cost) || 0) / (parseFloat(matForm.coverage_per_unit) || 1))}</strong>
                                        </p>
                                    )}
                                    <Button onClick={handleSaveMaterial} disabled={saving} className="w-full h-10 bg-red-600 hover:bg-red-700 text-white" data-testid="save-material-btn">
                                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                        Save Material
                                    </Button>
                                </CardContent>
                            </Card>
                        )}

                        {materials.length === 0 ? (
                            <Card><CardContent className="p-8 text-center text-slate-400">
                                <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                <p className="font-medium">No materials yet</p>
                                <p className="text-sm mt-1">Add materials to build up your price list</p>
                            </CardContent></Card>
                        ) : (
                            Object.entries(
                                materials.reduce((acc, m) => {
                                    const cat = m.category || 'Other';
                                    acc[cat] = acc[cat] || [];
                                    acc[cat].push(m);
                                    return acc;
                                }, {})
                            ).map(([cat, items]) => (
                                <Card key={cat} className="bg-white border-slate-200">
                                    <CardHeader className="pb-2 pt-3 px-4">
                                        <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-500">{cat}</CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <div className="divide-y divide-slate-100">
                                            {items.map(m => (
                                                <div key={m.id} className="flex items-center justify-between px-4 py-2.5 group" data-testid={`material-row-${m.id}`}>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-sm text-slate-900 truncate">{m.name}</p>
                                                        <p className="text-xs text-slate-500">
                                                            {fmt(m.unit_cost)} / {m.unit} &bull; {m.coverage_per_unit} m² coverage &bull; <strong>{fmt(m.cost_per_m2)}/m²</strong>
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-1 shrink-0">
                                                        <button onClick={() => openEditMaterial(m)} className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100">
                                                            <Edit2 className="h-3.5 w-3.5" />
                                                        </button>
                                                        <AlertDialog>
                                                            <AlertDialogTrigger asChild>
                                                                <button className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50">
                                                                    <Trash2 className="h-3.5 w-3.5" />
                                                                </button>
                                                            </AlertDialogTrigger>
                                                            <AlertDialogContent>
                                                                <AlertDialogHeader><AlertDialogTitle>Delete {m.name}?</AlertDialogTitle>
                                                                    <AlertDialogDescription>This will also remove it from all rate compositions.</AlertDialogDescription></AlertDialogHeader>
                                                                <AlertDialogFooter>
                                                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                    <AlertDialogAction onClick={() => handleDeleteMaterial(m.id)} className="bg-red-600">Delete</AlertDialogAction>
                                                                </AlertDialogFooter>
                                                            </AlertDialogContent>
                                                        </AlertDialog>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        )}
                    </TabsContent>

                    {/* ── TAB: COMPOSITIONS ──────────────────────────────────────────── */}
                    <TabsContent value="compositions" className="space-y-4 mt-4">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                            <strong>How it works:</strong> For each work type, specify materials (qty + wastage %) and labour hours per m². Click <strong>Save Compositions</strong> then <strong>Recalculate Rate Materials</strong> to update your rate card automatically.
                        </div>

                        {/* Out-of-sync banner */}
                        {outOfSyncCount > 0 && (
                            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-300 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4 text-amber-600 shrink-0" />
                                    <p className="text-sm text-amber-800">
                                        <strong>{outOfSyncCount} material {outOfSyncCount === 1 ? 'quantity' : 'quantities'}</strong> are out of sync with updated coverage. Highlighted in orange below.
                                    </p>
                                </div>
                                <Button onClick={syncAllToCoverage} size="sm" variant="outline"
                                    className="shrink-0 h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100 ml-3" data-testid="sync-all-btn">
                                    <RefreshCw className="h-3.5 w-3.5 mr-1" /> Sync All
                                </Button>
                            </div>
                        )}

                        {materials.length === 0 && (
                            <Card><CardContent className="p-6 text-center text-slate-400 text-sm">
                                Add materials on the Price List tab first before setting up compositions.
                            </CardContent></Card>
                        )}

                        {Object.entries(RATE_GROUPS).map(([groupName, rates]) => (
                            <Card key={groupName} className="bg-white border-slate-200">
                                <CardHeader className="pb-2">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-xs font-bold uppercase tracking-wider text-slate-500">{groupName}</CardTitle>
                                        <Button
                                            size="sm" variant="outline"
                                            onClick={() => openInlineAdd(inlineAddGroup === groupName ? null : groupName)}
                                            className={`h-7 text-xs px-2.5 ${inlineAddGroup === groupName ? 'border-red-300 text-red-600 bg-red-50' : 'border-slate-300 text-slate-600 hover:border-red-300 hover:text-red-600'}`}
                                            data-testid={`inline-add-${groupName.replace(/\s+/g,'-')}`}
                                        >
                                            {inlineAddGroup === groupName ? <><X className="h-3 w-3 mr-1" /> Cancel</> : <><Plus className="h-3 w-3 mr-1" /> New Work Type</>}
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-3 pt-0">
                                    {/* Inline quick-add work type form */}
                                    {inlineAddGroup === groupName && (
                                        <div className="border border-red-200 bg-red-50/30 rounded-lg p-3 space-y-2">
                                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">New Work Type — {groupName}</p>
                                            <Input
                                                value={inlineWorkTypeForm.name}
                                                onChange={e => setInlineWorkTypeForm(p => ({ ...p, name: e.target.value }))}
                                                placeholder="e.g. Overskim Walls Only, Monocouche Over Scratch..."
                                                className="h-9 border-slate-300 text-sm"
                                                data-testid="inline-wt-name"
                                            />
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Appears in Survey</label>
                                                    <select value={inlineWorkTypeForm.survey_context}
                                                        onChange={e => setInlineWorkTypeForm(p => ({ ...p, survey_context: e.target.value }))}
                                                        className="w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white">
                                                        <option value="wall">Wall work type</option>
                                                        <option value="ceiling">Ceiling work type</option>
                                                        <option value="both">Both walls & ceilings</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">Job Type</label>
                                                    <select value={inlineWorkTypeForm.job_type_compat}
                                                        onChange={e => setInlineWorkTypeForm(p => ({ ...p, job_type_compat: e.target.value }))}
                                                        className="w-full h-9 px-2 text-sm border border-slate-300 rounded-md bg-white">
                                                        <option value="Plastering">Plastering only</option>
                                                        <option value="Rendering">Rendering only</option>
                                                        <option value="Both">All job types</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-slate-400">After creating, add materials & labour below then hit Recalculate to set the rate.</p>
                                            <Button onClick={handleSaveInline} disabled={savingInline || !inlineWorkTypeForm.name.trim()}
                                                className="w-full h-8 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="inline-save-btn">
                                                {savingInline ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                                                Create Work Type
                                            </Button>
                                        </div>
                                    )}
                                    {/* Custom work types in this group */}
                                    {customWorkTypes.filter(wt => wt.group === groupName).map(wt => {
                                        const components = compositions[wt.rate_key] || [];
                                        const compTotal = components.reduce((sum, c) => {
                                            const mat = materials.find(m => m.id === c.material_id);
                                            const wastage = 1 + ((c.wastage_percent || 0) / 100);
                                            return sum + (mat ? mat.unit_cost * c.quantity_per_unit * wastage : 0);
                                        }, 0);
                                        const hrs = labourHours[wt.rate_key] || 0;
                                        const contextLabel = wt.survey_context === 'wall' ? 'Wall' : wt.survey_context === 'ceiling' ? 'Ceiling' : 'Wall & Ceiling';
                                        return (
                                            <div key={wt.id} className="border border-blue-200 rounded-lg p-3 bg-blue-50/20">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-semibold text-sm text-slate-800">{wt.name}</p>
                                                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded px-1.5 py-0.5 uppercase tracking-wide">custom</span>
                                                            <span className="text-[10px] text-slate-400">{contextLabel} · {wt.job_type_compat}</span>
                                                        </div>
                                                        <div className="flex gap-3 text-xs mt-0.5">
                                                            {compTotal > 0 && <span className="text-green-700 font-medium">Materials: {fmt(compTotal)}/m²</span>}
                                                            {hrs > 0 && <span className="text-blue-700 font-medium">Labour: {hrs} hrs/m²</span>}
                                                        </div>
                                                    </div>
                                                    <AlertDialog>
                                                        <AlertDialogTrigger asChild>
                                                            <button className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50">
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </AlertDialogTrigger>
                                                        <AlertDialogContent>
                                                            <AlertDialogHeader><AlertDialogTitle>Remove "{wt.name}"?</AlertDialogTitle>
                                                                <AlertDialogDescription>This work type will no longer appear in surveys. Existing rooms using it are unaffected.</AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                <AlertDialogAction onClick={() => handleDeleteCustomWorkType(wt.id)} className="bg-red-600">Remove</AlertDialogAction>
                                                            </AlertDialogFooter>
                                                        </AlertDialogContent>
                                                    </AlertDialog>
                                                </div>
                                                {/* Column headers */}
                                                {components.length > 0 && (
                                                    <div className="flex items-center gap-2 mb-1 px-1">
                                                        <span className="flex-1 text-[10px] font-bold text-slate-400 uppercase">Material</span>
                                                        <span className="w-24 text-[10px] font-bold text-slate-400 uppercase text-right">Qty/m²</span>
                                                        <span className="w-16 text-[10px] font-bold text-slate-400 uppercase text-right">Waste %</span>
                                                        <span className="w-6 shrink-0" />
                                                    </div>
                                                )}
                                                {components.map(comp => {
                                                    const mat = materials.find(m => m.id === comp.material_id);
                                                    const outSync = isOutOfSync(comp);
                                                    return (
                                                        <div key={comp.material_id} className={`flex items-center gap-2 mb-1.5 rounded-lg ${outSync ? 'bg-amber-50 border border-amber-200 p-1 -mx-1' : ''}`}>
                                                            <span className="flex-1 text-xs text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 truncate">
                                                                {mat?.name || 'Unknown'} <span className="text-slate-400">({mat?.unit})</span>
                                                            </span>
                                                            <div className="relative">
                                                                <Input type="number" step="0.0001" min="0" value={comp.quantity_per_unit}
                                                                    onChange={e => updateComponentQty(wt.rate_key, comp.material_id, e.target.value)}
                                                                    className={`w-24 h-8 text-sm text-right ${outSync ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`} />
                                                                {outSync && (
                                                                    <button onClick={() => syncComponentToCoverage(wt.rate_key, comp.material_id)}
                                                                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600">
                                                                        <RefreshCw className="h-2.5 w-2.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="relative w-16">
                                                                <Input type="number" step="1" min="0" max="50" value={comp.wastage_percent || 0}
                                                                    onChange={e => updateComponentWastage(wt.rate_key, comp.material_id, e.target.value)}
                                                                    className="w-16 h-8 text-sm text-right border-slate-300 pr-5" />
                                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                                                            </div>
                                                            <button onClick={() => removeComponent(wt.rate_key, comp.material_id)}
                                                                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-500 shrink-0">
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                                {materials.length > 0 && (
                                                    <select defaultValue=""
                                                        onChange={e => { if (e.target.value) { addComponent(wt.rate_key, e.target.value); e.target.value = ''; } }}
                                                        className="w-full h-8 px-2 text-xs border border-dashed border-slate-300 rounded text-slate-500 bg-white mt-1">
                                                        <option value="">+ Add material...</option>
                                                        {materials.filter(m => !components.some(c => c.material_id === m.id)).map(m => (
                                                            <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                                                        ))}
                                                    </select>
                                                )}
                                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-blue-100">
                                                    <span className="flex-1 text-xs font-medium text-blue-700">Labour hrs / m²</span>
                                                    <Input type="number" step="0.01" min="0"
                                                        value={labourHours[wt.rate_key] || ''}
                                                        onChange={e => setLabourHours(prev => ({ ...prev, [wt.rate_key]: parseFloat(e.target.value) || 0 }))}
                                                        placeholder="0.00" className="w-24 h-8 text-sm text-right border-blue-200 bg-blue-50/50" />
                                                    <span className="text-xs text-blue-500 w-6 shrink-0">hrs</span>
                                                    <span className="w-6 shrink-0" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {rates.map(rate => {
                                        const components = compositions[rate.key] || [];
                                        const compTotal = components.reduce((sum, c) => {
                                            const mat = materials.find(m => m.id === c.material_id);
                                            const wastage = 1 + ((c.wastage_percent || 0) / 100);
                                            return sum + (mat ? mat.unit_cost * c.quantity_per_unit * wastage : 0);
                                        }, 0);
                                        const hrs = labourHours[rate.key] || 0;
                                        return (
                                            <div key={rate.key} className="border border-slate-100 rounded-lg p-3 bg-slate-50/50">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div>
                                                        <p className="font-semibold text-sm text-slate-800">{rate.name}</p>
                                                        <div className="flex gap-3 text-xs mt-0.5">
                                                            {compTotal > 0 && <span className="text-green-700 font-medium">Materials: {fmt(compTotal)}/{rate.unit}</span>}
                                                            {hrs > 0 && <span className="text-blue-700 font-medium">Labour: {hrs} hrs/{rate.unit}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Column headers */}
                                                {components.length > 0 && (
                                                    <div className="flex items-center gap-2 mb-1 px-1">
                                                        <span className="flex-1 text-[10px] font-bold text-slate-400 uppercase">Material</span>
                                                        <span className="w-24 text-[10px] font-bold text-slate-400 uppercase text-right">Qty/{rate.unit}</span>
                                                        <span className="w-16 text-[10px] font-bold text-slate-400 uppercase text-right">Waste %</span>
                                                        <span className="w-6 shrink-0" />
                                                    </div>
                                                )}
                                                {/* Material component rows */}
                                                {components.map(comp => {
                                                    const mat = materials.find(m => m.id === comp.material_id);
                                                    const outOfSync = isOutOfSync(comp);
                                                    const expectedVal = expectedQty(comp.material_id);
                                                    return (
                                                        <div key={comp.material_id} className={`flex items-center gap-2 mb-1.5 rounded-lg ${outOfSync ? 'bg-amber-50 border border-amber-200 p-1 -mx-1' : ''}`}>
                                                            <span className="flex-1 text-xs text-slate-700 bg-white border border-slate-200 rounded px-2 py-1 truncate">
                                                                {mat?.name || 'Unknown'} <span className="text-slate-400">({mat?.unit})</span>
                                                            </span>
                                                            <div className="relative">
                                                                <Input type="number" step="0.0001" min="0"
                                                                    value={comp.quantity_per_unit}
                                                                    onChange={e => updateComponentQty(rate.key, comp.material_id, e.target.value)}
                                                                    className={`w-24 h-8 text-sm text-right ${outOfSync ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-300'}`}
                                                                    title={outOfSync ? `Out of sync — current coverage gives ${expectedVal}` : `${comp.quantity_per_unit} ${mat?.unit || 'units'} per ${rate.unit}`} />
                                                                {outOfSync && (
                                                                    <button onClick={() => syncComponentToCoverage(rate.key, comp.material_id)}
                                                                        className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-amber-500 text-white rounded-full flex items-center justify-center hover:bg-amber-600"
                                                                        title={`Reset to ${expectedVal} (from coverage)`}>
                                                                        <RefreshCw className="h-2.5 w-2.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="relative w-16">
                                                                <Input type="number" step="1" min="0" max="50"
                                                                    value={comp.wastage_percent || 0}
                                                                    onChange={e => updateComponentWastage(rate.key, comp.material_id, e.target.value)}
                                                                    className="w-16 h-8 text-sm text-right border-slate-300 pr-5"
                                                                    title="Wastage %" />
                                                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                                                            </div>
                                                            <button onClick={() => removeComponent(rate.key, comp.material_id)}
                                                                className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-red-500 shrink-0">
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                                {/* Add material */}
                                                {materials.length > 0 && (
                                                    <select defaultValue=""
                                                        onChange={e => { if (e.target.value) { addComponent(rate.key, e.target.value); e.target.value = ''; } }}
                                                        className="w-full h-8 px-2 text-xs border border-dashed border-slate-300 rounded text-slate-500 bg-white mt-1">
                                                        <option value="">+ Add material...</option>
                                                        {materials.filter(m => !components.some(c => c.material_id === m.id)).map(m => (
                                                            <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
                                                        ))}
                                                    </select>
                                                )}
                                                {/* Labour hours */}
                                                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                                                    <span className="flex-1 text-xs font-medium text-blue-700">Labour hrs / {rate.unit}</span>
                                                    <Input type="number" step="0.01" min="0"
                                                        value={labourHours[rate.key] || ''}
                                                        onChange={e => setLabourHours(prev => ({ ...prev, [rate.key]: parseFloat(e.target.value) || 0 }))}
                                                        placeholder="0.00"
                                                        className="w-24 h-8 text-sm text-right border-blue-200 bg-blue-50/50"
                                                        data-testid={`labour-hrs-${rate.key}`} />
                                                    <span className="text-xs text-blue-500 w-6 shrink-0">hrs</span>
                                                    <span className="w-6 shrink-0" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        ))}

                        <div className="pb-24" />
                    </TabsContent>
                </Tabs>
            </div>

            {/* Floating sticky action bar — compositions tab */}
            {activeTab === 'compositions' && (
                <div className="fixed bottom-16 lg:bottom-4 left-0 lg:left-64 right-0 z-50 flex justify-center pointer-events-none">
                    <div className="flex gap-2 w-full max-w-3xl mx-auto px-4 pointer-events-auto">
                        <Button
                            onClick={handleSaveCompositions}
                            disabled={saving || materials.length === 0}
                            className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20"
                            data-testid="save-compositions-btn"
                        >
                            {saving
                                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
                                : <><Save className="h-4 w-4 mr-2" />Save Compositions</>}
                        </Button>
                        <Button
                            onClick={handleRecalculate}
                            disabled={recalculating || materials.length === 0}
                            className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20"
                            data-testid="recalculate-btn"
                        >
                            {recalculating
                                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Recalculating...</>
                                : <><RefreshCw className="h-4 w-4 mr-2" />Recalculate Rates</>}
                        </Button>
                    </div>
                </div>
            )}
        </Layout>
    );
};
